import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SectionCard } from '@/components/ui/section-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/ui/empty-state';
import { EventCard } from '@/components/events/EventCard';
import { DaySheetView } from '@/components/events/DaySheetView';
import { ResultsView } from '@/components/events/ResultsView';
import { useJackpotDetail } from '@/api/extensions/useJackpotOps';
import { useMyRegistrations } from '@/api/extensions/useRegistrationOps';
import { useUserHandicaps } from '@/hooks/useUserHandicaps';
import { formatDateRange } from '@/utils/formatters';

export default function JackpotDetailPage() {
  const { jackpotId } = useParams<{ jackpotId: string }>();
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('events');

  const { data: jackpot, isLoading, isError } = useJackpotDetail(jackpotId!);
  const { data: myRegistrations } = useMyRegistrations();
  const { data: userHandicaps } = useUserHandicaps();
  const { setTrail } = useBreadcrumb();

  // Set breadcrumb trail: Jackpots > [Jackpot Name]
  useEffect(() => {
    if (jackpot?.name) {
      setTrail([
        { label: 'Jackpots', path: '/jackpots' },
        { label: jackpot.name, path: `/jackpots/${jackpotId}` },
      ]);
    }
    return () => setTrail(null);
  }, [jackpotId, jackpot?.name, setTrail]);

  // Build a map of event_id → registration. Skip cancelled rows so the
  // EventCard "registered" highlight doesn't survive an unregister —
  // `useMyRegistrations()` returns cancelled rows alongside active ones.
  const registrationMap = new Map<string, any>();
  if (myRegistrations) {
    for (const reg of myRegistrations) {
      const status = (reg as any).trig_status_name ?? (reg as any).status;
      if (status === 'cancelled') continue;
      registrationMap.set(reg.event_id, reg);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !jackpot) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/jackpots')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Jackpots
        </Button>
        <EmptyState
          icon={Calendar}
          title="Jackpot not found"
          description="This jackpot may have been removed or is not published."
          action={{ label: 'Browse Jackpots', onClick: () => navigate('/jackpots') }}
        />
      </div>
    );
  }

  // Redirect if not a jackpot
  if ((jackpot as any).rodeo_or_jackpot && (jackpot as any).rodeo_or_jackpot !== 'jackpot') {
    navigate(`/rodeos/${jackpotId}`, { replace: true });
    return null;
  }

  const events: any[] = (jackpot as any).events ?? [];
  const description = (jackpot as any).description ?? '';

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/jackpots')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Jackpots
      </Button>

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-bold">{jackpot.name}</h1>
        <Badge variant="secondary">
          {events.length} event{events.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Info card */}
      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            {((jackpot as any).start_date || (jackpot as any).end_date) && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4 shrink-0" />
                <span>
                  {formatDateRange(
                    (jackpot as any).start_date,
                    (jackpot as any).end_date,
                  )}
                </span>
              </div>
            )}
            {(jackpot as any).start_time && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                <span>{(jackpot as any).start_time}</span>
              </div>
            )}
            {(jackpot as any).location && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>{(jackpot as any).location}</span>
              </div>
            )}
          </div>

          {description && (
            <div className="mt-4">
              <button
                className="flex items-center gap-1 text-sm font-medium text-primary"
                onClick={() => setInfoOpen(!infoOpen)}
              >
                {infoOpen ? 'Hide' : 'Show'} details
                {infoOpen ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
              {infoOpen && (
                <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
                  {description}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="daysheets">
            Day Sheets
          </TabsTrigger>
          <TabsTrigger value="results">
            Results
          </TabsTrigger>
        </TabsList>
        <TabsContent value="events" className="mt-4">
          <SectionCard
            title="Events"
            headerRight={
              <span className="text-xs opacity-70">
                {events.length} event{events.length !== 1 ? 's' : ''}
              </span>
            }
          >
            {events.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No events yet"
                description="Events for this jackpot haven't been published yet."
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {events.map((event: any) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    registrationStatus={registrationMap.get(event.id) ?? null}
                    userHandicaps={userHandicaps ?? []}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </TabsContent>
        <TabsContent value="daysheets" className="mt-4">
          <DaySheetView events={events} />
        </TabsContent>
        <TabsContent value="results" className="mt-4">
          <ResultsView events={events} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
